import React, { useEffect, useRef, useState } from "react";
import styles from "../Lesson.module.css";
import { useAppTranslation } from "./TranslationContext";

export const CustomVideoPlayer = ({ src, checkpoints = [], onComplete, videoId }) => {
  const videoRef = useRef(null);
  const shownRef = useRef(new Set());
  const lastTimeRef = useRef(0);
  const completedRef = useRef(false);
  const { t } = useAppTranslation();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const [progress, setProgress] = useState(0);
  const [timeDisplay, setTimeDisplay] = useState("0:00 / --:--");
  const [duration, setDuration] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCheckpoint, setActiveCheckpoint] = useState(null);
  const [answer, setAnswer] = useState("");

  const storageKey = `video_progress_${videoId || src}`;
  const [savedTime, setSavedTime] = useState(0);
  const [showResumeOptions, setShowResumeOptions] = useState(false);

  // ---------- helpers ----------
  const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds <= 0) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const readDurationSafely = () => {
    const v = videoRef.current;
    if (!v) return 0;
    const d = v.duration;
    if (!isFinite(d) || isNaN(d) || d <= 0) return 0;
    return d;
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    const video = videoRef.current;
    if (video) {
      video.volume = v;
      video.muted = v === 0;
      setIsMuted(video.muted);
    }
  };

  const handleFullscreen = () => {
    const container = videoRef.current?.parentElement;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  const showModal = (cp) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setIsPlaying(false);
    setIsModalOpen(true);
    setActiveCheckpoint(cp);
    setAnswer("");
  };

  // ---------- initial read from localStorage ----------
  useEffect(() => {
    shownRef.current = new Set();
    lastTimeRef.current = 0;
    setIsModalOpen(false);
    setActiveCheckpoint(null);
    setAnswer("");

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const t = parseFloat(stored);
        if (!isNaN(t) && t > 0) {
          setSavedTime(t);
          setShowResumeOptions(true);
        } else {
          setSavedTime(0);
          setShowResumeOptions(false);
        }
      } else {
        setSavedTime(0);
        setShowResumeOptions(false);
      }
    } catch (e) {
      console.warn("localStorage read error:", e);
      setSavedTime(0);
      setShowResumeOptions(false);
    }
  }, [src, checkpoints, storageKey]);

  // ---------- load metadata & watch duration changes ----------
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // просим подгрузить метаданные; актуально при смене src
    v.load();

    const handleLoadedMetadata = () => {
      const d = readDurationSafely();
      setDuration(d);
      // правая часть времени корректна сразу после метаданных
      setTimeDisplay(`${formatTime(v.currentTime || 0)} / ${formatTime(d)}`);

      // повторная валидация сохранённого времени
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const t = parseFloat(stored);
          if (!isNaN(t) && t > 0 && d && t < d - 1) {
            setSavedTime(t);
            setShowResumeOptions(true);
          } else {
            localStorage.removeItem(storageKey);
            setSavedTime(0);
            setShowResumeOptions(false);
          }
        }
      } catch {}
    };

    const handleDurationChange = () => {
      const d = readDurationSafely();
      setDuration(d);
      setTimeDisplay(`${formatTime(v.currentTime || 0)} / ${formatTime(d)}`);
    };

    // если метаданные уже доступны к моменту подписки
    if (v.readyState >= 1) handleLoadedMetadata();

    v.addEventListener("loadedmetadata", handleLoadedMetadata);
    v.addEventListener("durationchange", handleDurationChange);

    return () => {
      v.removeEventListener("loadedmetadata", handleLoadedMetadata);
      v.removeEventListener("durationchange", handleDurationChange);
    };
  }, [src, storageKey]);

  // ---------- core player events (timeupdate/pause/ended) ----------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const maybeComplete = () => {
      const d = readDurationSafely();
      if (!d || completedRef.current) return;
      if (video.currentTime >= d - 0.2) {
        completedRef.current = true;
        setIsPlaying(false);
        try {
          localStorage.removeItem(storageKey);
        } catch {}
        if (onComplete) onComplete();
      }
    };

    const onTimeUpdate = () => {
      const d = duration || readDurationSafely();
      const cur = video.currentTime || 0;

      setProgress(d ? (cur / d) * 100 : 0);
      setTimeDisplay(`${formatTime(cur)} / ${formatTime(d)}`);

      checkpoints.forEach((cp) => {
        if (!shownRef.current.has(cp.time) && lastTimeRef.current < cp.time && cur >= cp.time) {
          shownRef.current.add(cp.time);
          showModal(cp);
        }
      });

      if (Math.abs(cur - lastTimeRef.current) >= 2) {
        try {
          localStorage.setItem(storageKey, String(cur));
        } catch {}
      }

      lastTimeRef.current = cur;
      maybeComplete();
    };

    const onPause = () => {
      try {
        localStorage.setItem(storageKey, String(video.currentTime || 0));
      } catch {}
    };

    const onEnded = () => {
      setIsPlaying(false);
      maybeComplete();
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    const saveAndClose = () => {
      try {
        if (videoRef.current) {
          localStorage.setItem(storageKey, String(videoRef.current.currentTime || 0));
        }
      } catch {}
    };
    const handleVisibility = () => {
      if (document.hidden) saveAndClose();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", saveAndClose);

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", saveAndClose);
    };
  }, [checkpoints, onComplete, storageKey, duration]);

  // ---------- body scroll lock for modal ----------
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = prev || "auto";
    };
  }, [isModalOpen]);

  // ---------- submit answer ----------
  const handleSubmit = (e) => {
    e.preventDefault();
    const video = videoRef.current;
    if (!video || !activeCheckpoint) return;

    const ok =
      answer.trim().toLowerCase() === (activeCheckpoint.answer || "").toLowerCase();

    setIsModalOpen(false);

    if (ok) {
      setActiveCheckpoint(null);
      video.play();
      setIsPlaying(true);
    } else {
      const currentIndex = checkpoints.findIndex((cp) => cp.id === activeCheckpoint.id);
      const fallbackTime = currentIndex > 0 ? checkpoints[currentIndex - 1].time : 0;

      shownRef.current.delete(activeCheckpoint.time);
      video.currentTime = fallbackTime;
      lastTimeRef.current = video.currentTime;

      setActiveCheckpoint(null);
      video.play();
      setIsPlaying(true);
    }
  };

  // ---------- render ----------
  return (
    <div className={styles.playerContainer}>
      <video
        ref={videoRef}
        src={src}
        className={styles.video}
        preload="metadata"
        playsInline
      />

      {/* Resume overlay */}
      {!isPlaying && !isModalOpen && showResumeOptions && (
        <div className={styles.resumeOverlay}>
          <p>{t("alreadyWatched")}</p>
          <div className={styles.resumeBtns}>
            <button
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                v.currentTime = 0;
                v.play();
                setIsPlaying(true);
                setShowResumeOptions(false);
              }}
            >
              {t("startOver")}
            </button>
            <button
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                if (v.readyState >= 1) v.currentTime = savedTime || 0;
                v.play();
                setIsPlaying(true);
                setShowResumeOptions(false);
              }}
            >
              {t("continueWatching")} ({formatTime(Math.max(0, savedTime))})
            </button>
          </div>
        </div>
      )}

      {/* Start button */}
      {!isPlaying && !isModalOpen && !showResumeOptions && (
        <button
          className={styles.startButton}
          onClick={() => {
            videoRef.current?.play();
            setIsPlaying(true);
          }}
        >
          ▶
        </button>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>
        <div className={styles.buttonRow}>
          <div className={styles.leftControls}>
            <button onClick={togglePlay}>{isPlaying ? "⏸" : "▶"}</button>
            <button onClick={toggleMute}>{isMuted ? "🔇" : "🔊"}</button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolume}
              className={styles.volumeSlider}
            />
            <span className={styles.timeDisplay}>
              {timeDisplay /* e.g. 0:13 / 4:20 or 0:00 / --:-- */}
            </span>
          </div>
          <div className={styles.rightControls}>
            <button onClick={handleFullscreen}>⛶</button>
          </div>
        </div>
      </div>

      {/* Checkpoint modal */}
      {isModalOpen && activeCheckpoint && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>{t("question")}</h3>
            <p>{activeCheckpoint.question}</p>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder={t("enterAnswer")}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                required
              />
              <button className="register__btn" type="submit">
                {t("sendAll")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
